import type { FaqItem } from '@/components/common/FaqList'
import type { Lang } from '@/types/lang'
import {
  ANTIGRAVITY_CONFIG,
  HERMES_ADD_COMMAND,
  HERMES_GUIDE_URL,
  MCP_URL,
  type ClientId,
  type GuideStep,
  type PromptExample,
} from './guideContent'

// Тексты гайда подключения на двух языках интерфейса. Это не плоские строки, а
// списки (шаги, примеры, вопросы), поэтому вместо `defineMessages` — пара
// объектов одной формы и выбор по языку в `guideFor`.

const NAME = 'OA Studio'
const CHATGPT_PLUGINS_URL = 'https://chatgpt.com/plugins'
const LOGIN_RU = 'войдите в студию как автор и нажмите «Разрешить»'

// Шаги сверены с официальной документацией сервисов 2 октября 2026.
const STEPS_RU: Record<ClientId, GuideStep[]> = {
  claude: [
    { title: 'Откройте Customize → Connectors, нажмите «+ Add» и выберите Add custom connector.' },
    {
      title: `Введите название, например «${NAME}», вставьте адрес ниже и нажмите Continue.`,
      copy: MCP_URL,
    },
    { title: `Выберите вход Sign in now, а в открывшемся окне ${LOGIN_RU}.` },
    { title: 'Готово, чтобы включить коннектор в чате, нажмите «+» и выберите Connectors.' },
  ],
  code: [
    {
      title: 'Добавьте коннектор командой:',
      copy: `claude mcp add --transport http academy ${MCP_URL}`,
    },
    { title: `Наберите /mcp, выберите сервер academy, а в открывшемся браузере ${LOGIN_RU}.` },
    { title: 'Готово, проверить подключение можно командой claude mcp list.' },
  ],
  chatgpt: [
    {
      title:
        'Откройте «Плагины» в меню слева, нажмите «Добавить» и выберите «Добавить пользовательский сервер MCP».',
      link: { href: CHATGPT_PLUGINS_URL, label: 'Открыть «Плагины» в ChatGPT' },
    },
    {
      title: `В поле «Название» введите «${NAME}», в «Подключение» вставьте адрес ниже, аутентификация — OAuth.`,
      copy: MCP_URL,
    },
    { title: 'Отметьте «Я понимаю и хочу продолжить» и нажмите «Создать в виде плагина».' },
    { title: `В открывшемся окне входа ${LOGIN_RU}.` },
    { title: `Готово, ${NAME} появится в ваших плагинах и в чате.` },
  ],
  antigravity: [
    {
      title:
        'Откройте настройки Google Antigravity → Customizations, найдите Installed MCP Servers и нажмите Open MCP Config.',
    },
    {
      title:
        'В файле mcp_config.json добавьте внутрь блока "mcpServers" эту строку (другие серверы не трогайте) и сохраните файл:',
      copy: ANTIGRAVITY_CONFIG,
    },
    {
      title:
        'Если сервер не появился, закройте настройки и откройте снова. Рядом с ним нажмите Authenticate.',
    },
    {
      title: `В открывшемся браузере ${LOGIN_RU}. Antigravity покажет код — скопируйте его, вставьте в поле авторизации и нажмите Submit.`,
    },
    { title: `Готово, ${NAME} появится среди MCP-серверов Antigravity.` },
  ],
  // Шаги — по гайду сообщества (learn.open-academy.app, 13.10.2026).
  hermes: [
    {
      title:
        'Добавьте коннектор командой в терминале — в том же профиле Hermes, где работает агент, — и следуйте подсказкам:',
      copy: HERMES_ADD_COMMAND,
    },
    {
      title: `Hermes откроет браузер: ${LOGIN_RU}. Если браузер не открылся, перейдите по ссылке из терминала. Войти заново:`,
      copy: 'hermes mcp login oa-studio',
    },
    {
      title:
        'Проверьте подключение, затем откройте новую сессию Hermes или наберите в ней /reload-mcp:',
      copy: 'hermes mcp test oa-studio',
    },
    {
      title:
        'Если Hermes работает на удалённом сервере или через Telegram, загляните в подробную инструкцию:',
      link: { href: HERMES_GUIDE_URL.ru, label: 'Пошаговый гайд' },
    },
  ],
}

const PROMPTS_RU: PromptExample[] = [
  {
    topic: 'Финансы',
    text: 'Сделай курс про сложный процент: хочу, чтобы человек понял, как деньги растут со временем. 6 уроков, больше примеров с цифрами, картинок и квизов.',
  },
  {
    topic: 'История',
    text: 'Сделай курс про историю Таиланда: хочу рассказать о стране от первых королевств до наших дней. 6 уроков, с картами, датами и квизом в конце каждого урока.',
  },
  {
    topic: 'Продажи',
    text: 'Сделай курс про работу с возражениями для новых менеджеров по продажам. 5 уроков, разбор живых диалогов, квиз после каждого и итоговый кейс.',
  },
]

const FAQ_RU: FaqItem[] = [
  {
    question: 'Что такое коннектор?',
    answer:
      'Коннектор — это мост между вашей нейросетью и OA Studio (в технических описаниях его называют MCP): после подключения агент получает инструменты студии и создаёт курсы, уроки и блоки прямо из чата, а доступ можно отозвать в любой момент на вкладке «Подключённые».',
  },
  {
    question: 'Нужна ли платная подписка на нейросеть (Claude, ChatGPT)?',
    answer:
      'Зависит от нейросети: в ChatGPT коннекторы работают только на платных тарифах, а в Claude есть даже на бесплатном (один). Условия сервисов меняются, поэтому сверьтесь с их справкой.',
  },
  {
    question: 'Кто платит за создание курса через коннектор?',
    answer:
      'Текст курса и промпты для картинок и видео пишет ваша подписка на нейросеть, а сами медиа (картинки, видео и озвучка) создаются за кредиты OA Studio, которые можно купить в разделе «Использование». Любое медиа вы всегда можете сделать сами и добавить в курс вручную.',
  },
  {
    question: 'Что делаю я, а что делает ИИ-агент?',
    answer:
      'Вы придумываете идею курса и даёте агенту контекст и материалы, а он собирает черновик, раскладывает всё по урокам и блокам и правит по вашим замечаниям. Публикацией и удалением курса вы управляете сами, на странице курса в студии.',
  },
  {
    question: 'Кто может подключить коннектор?',
    answer:
      'Только автор: на шаге «Разрешить» нужно войти в студию аккаунтом автора, обычный пользователь получит отказ.',
  },
  {
    question: 'Можно ли подключить сразу несколько нейросетей?',
    answer:
      'Да, каждая нейросеть подключается отдельно, и все подключения видны на вкладке «Подключённые».',
  },
  {
    question: 'Что, если агент сделал что-то не так?',
    answer:
      'Правки живого курса попадают в черновик и выйдут к ученикам только после вашего «Обновить», а от ненужных правок можно отказаться в окне публикации кнопкой «Вернуть опубликованную версию».',
  },
  {
    question: 'Как отключить коннектор?',
    answer:
      'Откройте вкладку «Подключённые» и отзовите доступ у нужного коннектора, после этого агент перестанет видеть ваши курсы.',
  },
  {
    question: 'Пишет, что коннектор устарел',
    answer: 'Перезапустите чат или переподключите коннектор.',
  },
]

const LOGIN_EN = 'sign in to the studio as an author and click “Allow”'

const STEPS_EN: Record<ClientId, GuideStep[]> = {
  claude: [
    { title: 'Open Customize → Connectors, click “+ Add” and choose Add custom connector.' },
    {
      title: `Enter a name, for example “${NAME}”, paste the URL below and click Continue.`,
      copy: MCP_URL,
    },
    { title: `Choose Sign in now, and in the window that opens ${LOGIN_EN}.` },
    { title: 'Done — to enable the connector in a chat, click “+” and choose Connectors.' },
  ],
  code: [
    {
      title: 'Add the connector with this command:',
      copy: `claude mcp add --transport http academy ${MCP_URL}`,
    },
    { title: `Type /mcp, select the academy server, and in the browser that opens ${LOGIN_EN}.` },
    { title: 'Done — you can check the connection with claude mcp list.' },
  ],
  chatgpt: [
    {
      title: 'Open Plugins in the left menu, click Add and choose Add custom MCP server.',
      link: { href: CHATGPT_PLUGINS_URL, label: 'Open Plugins in ChatGPT' },
    },
    {
      title: `Enter “${NAME}” as the name, paste the URL below into Connection, and set Authentication to OAuth.`,
      copy: MCP_URL,
    },
    { title: 'Check “I understand and want to continue” and click Create as a plugin.' },
    { title: `In the sign-in window that opens, ${LOGIN_EN}.` },
    { title: `Done — ${NAME} will appear in your plugins and in the chat.` },
  ],
  antigravity: [
    {
      title:
        'Open Google Antigravity settings → Customizations, find Installed MCP Servers and click Open MCP Config.',
    },
    {
      title:
        'In mcp_config.json, add this line inside the "mcpServers" block (leave other servers as they are) and save the file:',
      copy: ANTIGRAVITY_CONFIG,
    },
    {
      title:
        'If the server doesn’t appear, close the settings and open them again. Click Authenticate next to it.',
    },
    {
      title: `In the browser that opens, ${LOGIN_EN}. Antigravity will show a code — copy it, paste it into the authorization field and click Submit.`,
    },
    { title: `Done — ${NAME} will appear among Antigravity’s MCP servers.` },
  ],
  hermes: [
    {
      title:
        'Add the connector with this terminal command — in the same Hermes profile the agent runs in — and follow the prompts:',
      copy: HERMES_ADD_COMMAND,
    },
    {
      title: `Hermes will open a browser: ${LOGIN_EN}. If no browser opens, follow the link from the terminal. To sign in again:`,
      copy: 'hermes mcp login oa-studio',
    },
    {
      title:
        'Check the connection, then start a new Hermes session or type /reload-mcp in the current one:',
      copy: 'hermes mcp test oa-studio',
    },
    {
      title: 'If Hermes runs on a remote server or through Telegram, check the detailed guide:',
      link: { href: HERMES_GUIDE_URL.en, label: 'Step-by-step guide' },
    },
  ],
}

const PROMPTS_EN: PromptExample[] = [
  {
    topic: 'Finance',
    text: 'Make a course on compound interest: I want people to understand how money grows over time. 6 lessons, more examples with numbers, images and quizzes.',
  },
  {
    topic: 'History',
    text: 'Make a course on the history of Thailand: I want to tell the story of the country from the first kingdoms to the present day. 6 lessons, with maps, dates and a quiz at the end of each lesson.',
  },
  {
    topic: 'Sales',
    text: 'Make a course on handling objections for new sales managers. 5 lessons, breakdowns of real conversations, a quiz after each one and a final case study.',
  },
]

const FAQ_EN: FaqItem[] = [
  {
    question: 'What is a connector?',
    answer:
      'A connector is a bridge between your AI assistant and OA Studio (technical docs call it MCP): once connected, the agent gets the studio’s tools and creates courses, lessons and blocks right from the chat. You can revoke access at any time on the “Connected” tab.',
  },
  {
    question: 'Do I need a paid AI subscription (Claude, ChatGPT)?',
    answer:
      'It depends on the assistant: in ChatGPT connectors only work on paid plans, while Claude allows one connector even on the free plan. Terms change, so check their help pages.',
  },
  {
    question: 'Who pays for creating a course through the connector?',
    answer:
      'The course text and the prompts for images and videos come from your AI subscription, while the media itself (images, video and voiceover) is created with OA Studio credits, which you can buy in the “Usage” section. You can always make any media yourself and add it to the course manually.',
  },
  {
    question: 'What do I do, and what does the AI agent do?',
    answer:
      'You come up with the course idea and give the agent context and materials; it builds a draft, lays everything out into lessons and blocks, and makes edits based on your feedback. You control publishing and deleting the course yourself, on the course page in the studio.',
  },
  {
    question: 'Who can set up the connector?',
    answer:
      'Only an author: at the “Allow” step you need to sign in to the studio with an author account — regular users will be denied access.',
  },
  {
    question: 'Can I connect several AI assistants at once?',
    answer:
      'Yes, each assistant is connected separately, and all connections are listed on the “Connected” tab.',
  },
  {
    question: 'What if the agent got something wrong?',
    answer:
      'Edits to a live course go into a draft and reach learners only after you click “Update”, and you can discard unwanted edits in the publish window with “Revert to published version”.',
  },
  {
    question: 'How do I disconnect a connector?',
    answer:
      'Open the “Connected” tab and revoke access for the connector — after that the agent will no longer see your courses.',
  },
  {
    question: 'It says my connector is outdated',
    answer: 'Restart the chat or reconnect the connector.',
  },
]

interface GuideTexts {
  steps: Record<ClientId, GuideStep[]>
  prompts: PromptExample[]
  faq: FaqItem[]
}

const GUIDE: Record<Lang, GuideTexts> = {
  ru: { steps: STEPS_RU, prompts: PROMPTS_RU, faq: FAQ_RU },
  en: { steps: STEPS_EN, prompts: PROMPTS_EN, faq: FAQ_EN },
}

/** Тексты гайда на языке интерфейса (язык берёт вызывающий из `useUiLang`). */
export const guideFor = (lang: Lang): GuideTexts => GUIDE[lang]
